import axios from '../axiosInstance';
import { createCrudApi } from '../createCrudApi';
import { PaginatedResponse } from '../types';

// --- Helper for read-only endpoints (get list + getOneById) ---

function createReadApi<TItem, TGetParams extends Record<string, unknown> = Record<string, unknown>>(
  endpoint: string
) {
  return {
    get: (params?: TGetParams) => axios.get<PaginatedResponse<TItem>>(endpoint, { params }),
    getOneById: (id: number | string) => axios.get<TItem>(`${endpoint}${id}/`)
  };
}

// --- Common types ---

type BaseGetParams = {
  limit?: number;
  offset?: number;
  search?: string;
};

// --- ATC Groups ---

export type AtcGroupsGetParams = BaseGetParams & {
  code?: string;
  name?: string;
};

export type AtcGroup = {
  id: number;
  code: string;
  name: string;
  name_en: string;
  atc_type: string;
};

// --- Diagnoses ---

export type DiagnosesGetParams = BaseGetParams & {
  code?: string;
  clinical_pharmacology?: boolean;
};

export type Diagnosis = {
  id: number;
  code: string;
  name: string | null;
};

// --- Drugs ---

export type DrugsGetParams = BaseGetParams & {
  code_sukl?: string;
  name?: string;
};

export type Drug = {
  id: number;
  code_sukl: string;
  name: string;
  name_supplement: string;
  atc_group: string;
  is_active: boolean;
};

// --- Provider Departments ---

export type ProviderDepartmentGetParams = BaseGetParams;

export type ProviderDepartment = {
  id: number;
  name: string;
  icp: string;
  specialization_code: string;
  is_active: boolean;
  organization: number;
  organization_name: string;
};

// --- External Departments ---

export type ExternalDepartmentsGetParams = BaseGetParams;

export type ExternalDepartment = {
  id: number;
  actual_organization: string;
  icp: string;
  organization: string | null;
  department: string;
  specialization_code: string;
  is_active: boolean;
  external_organization: number | null;
};

// --- External Organizations ---

export type ExternalOrganizationsGetParams = BaseGetParams;

export type ExternalOrganization = {
  id: number;
  icz: string;
  name: string;
  is_active: boolean;
  valid_from: string | null;
  valid_to: string | null;
};

export type ExternalOrganizationLite = {
  icz: string;
  name: string;
};

// --- Insurance Companies ---

export type InsurancesGetParams = BaseGetParams;

export type InsuranceCompany = {
  id: number;
  code: string;
  name: string;
};

// --- Persons ---

export type PersonsGetParams = BaseGetParams;

export type Person = {
  id: number;
  person_number: string;
  name: string;
  f_title: string;
  l_title: string;
};

// --- Employees ---

export type EmployeesGetParams = BaseGetParams;

export type Employee = {
  id: number;
  external_id: string | null;
  name: string;
  card_number: string | null;
  personal_number: string | null;
  signature: string | null;
  valid_from: string;
  valid_to: string | null;
  is_active: boolean;
  department: number | null;
};

// --- Examining Physicians ---

export type ExaminingPhysiciansGetParams = BaseGetParams; 

export type ExaminingPhysician = {
  id: number;
  name: string;
  is_active: boolean;
  organization: number;
  organization_name?: string;
  valid_from: string;
  valid_to: string | null;
};  

// --- Applicants ---

export type ApplicantsGetParams = BaseGetParams & {
  icp?: string;
  icz?: string;
  name?: string;
};

export type Applicant = {
  id: number;
  external_department: {
    icp: string;
    department: string;
  };
  external_organization: {
    icz: string;
    name: string;
  };
  external_id: number;
  name: string;
  description: string;
  ico: string;
  dic: string;
  expertise: string;
  address_line1: string;
  address_line2: string;
  country: string;
  contact: string;
  contact_person: string;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
};

export type ApplicantLite = {
  id: number;
  name: string;
  external_department: {
    icp: string;
    department: string;
  };
  external_organization: {
    icz: string;
    name: string;
  };
};

// --- Payment Types ---

export type PaymentTypesGetParams = BaseGetParams;

export type PaymentType = {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
};

// --- Death Classifications ---

export type DeathClassificationsGetParams = BaseGetParams;

export type DeathClassification = {
  id: number;
  name: string;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
};

// --- Examination Types ---

export type ExaminationTypesGetParams = BaseGetParams;

export type ExaminationType = {
  id: number;
  code: string;
  name: string;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
};

// --- Genders ---

export type GendersGetParams = BaseGetParams;

export type Gender = {
  id: number;
  code: string;
  name: string;
};

// --- Patient Statuses ---

export type PatientStatusesGetParams = BaseGetParams;

export type PatientStatus = {
  id: number;
  name: string;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
};

// --- Toxicologists ---

export type ToxicologistsGetParams = BaseGetParams;

export type LiteEmployee = {
  id: number;
  name: string;
  external_id: string | null;
  card_number: string | null;
};

export type Toxicologist = {
  id: number;
  employee: LiteEmployee;
};

// --- API ---

export const ReferencesApi = () => ({
  // Read-only resources
  atcGroups: createReadApi<AtcGroup, AtcGroupsGetParams>('/references/atc-groups/'),
  diagnoses: createReadApi<Diagnosis, DiagnosesGetParams>('/references/diagnoses/'),
  drugs: createReadApi<Drug, DrugsGetParams>('/references/drugs/'),
  insurances: createCrudApi<InsuranceCompany, InsuranceCompany, InsurancesGetParams>('/references/insurances/'),
  persons: createCrudApi<Person, Person, PersonsGetParams>('/references/persons/'),
  providerDepartments: createCrudApi<ProviderDepartment, ProviderDepartment, ProviderDepartmentGetParams>(
    '/references/provider_departments/'
  ),
  employees: createCrudApi<Employee, Employee, EmployeesGetParams>('/references/employees/'),
  examiningPhysicians: createCrudApi<ExaminingPhysician, ExaminingPhysician, ExaminingPhysiciansGetParams>(
    '/references/examining_physician/'
  ),
  paymentTypes: createCrudApi<PaymentType, PaymentType, PaymentTypesGetParams>('/references/payment_types/'),
  examinationTypes: createCrudApi<ExaminationType, ExaminationType, ExaminationTypesGetParams>(
    '/references/examination_types/'
  ),
  genders: {
    get: (params?: GendersGetParams) =>
      axios.get<PaginatedResponse<Gender>>('/references/genders/', { params }),
    getOneByValue: (value: string) => axios.get<Gender>(`/references/genders/${value}/`)
  },
  patientStatuses: createCrudApi<PatientStatus, PatientStatus, PatientStatusesGetParams>(
    '/references/patient_statuses/'
  ),

  // Full CRUD resources
  externalDepartments: createCrudApi<
    ExternalDepartment,
    ExternalDepartment,
    ExternalDepartmentsGetParams
  >('/references/external_departments/'),

  externalOrganizations: createCrudApi<
    ExternalOrganization,
    ExternalOrganization,
    ExternalOrganizationsGetParams
  >('/references/external_organizations/'),

  applicants: {
    ...createCrudApi<Applicant, Applicant, ApplicantsGetParams>('/references/applicant/'),
    getLite: (params?: ApplicantsGetParams) =>
      axios.get<PaginatedResponse<ApplicantLite>>('/references/applicants/lite/', { params })
  },

  deathClassifications: createCrudApi<
    DeathClassification,
    DeathClassification,
    DeathClassificationsGetParams
  >('/references/death_classification/'),

  toxicologists: createCrudApi<Toxicologist, Toxicologist, ToxicologistsGetParams>(
    '/references/toxicologist/'
  )
});
